import { Reveal } from "../landing/Reveal";
import { listUseCases } from "../../use-cases/registry";
import { hrefForUseCase } from "../../use-cases/links";
import {
  TS_CASE_STUDY_HREF,
  TS_CUSTOMER,
  TS_HEADLINE,
} from "../../use-cases/case-studies/teachingStrategies";
import type { UseCaseSlug } from "../../use-cases/types";

interface MoreUseCasesProps {
  /** The page this renders on; its own card is left out. */
  currentSlug?: UseCaseSlug;
  /** Add a card for the Teaching Strategies case study. */
  includeCaseStudy?: boolean;
  headline?: string;
}

interface Card {
  href: string;
  label: string;
  teaser: string;
  cta: string;
}

/**
 * Cross-link cards to the other use-case pages (and, optionally, the
 * Teaching Strategies case study), so every marketing page leads on to the
 * rest of the site.
 */
export const MoreUseCases = ({
  currentSlug,
  includeCaseStudy = false,
  headline = "More ways teams use Forward",
}: MoreUseCasesProps) => {
  const cards: Card[] = listUseCases()
    .filter((u) => u.slug !== currentSlug)
    .map((u) => ({
      href: hrefForUseCase(u.slug),
      label: u.menuLabel,
      teaser: u.teaser,
      cta: `Explore ${u.menuLabel}`,
    }));
  if (includeCaseStudy) {
    cards.push({
      href: TS_CASE_STUDY_HREF,
      label: `Case study · ${TS_CUSTOMER}`,
      teaser: TS_HEADLINE,
      cta: "Read the case study",
    });
  }

  return (
    <section
      aria-labelledby="more-use-cases-heading"
      className="bg-fwd-soft py-20 md:py-28 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <h2
            id="more-use-cases-heading"
            className="font-display text-[clamp(1.8rem,3vw,2.6rem)] font-normal leading-tight text-fwd-ink mb-10 md:mb-12"
          >
            {headline}
          </h2>
        </Reveal>
        {/* Four cards read as a roomy 2×2 rather than a cramped row or
            three plus an orphan. */}
        <ul
          className={`grid grid-cols-1 gap-5 md:gap-6 ${
            cards.length === 4 ? "md:grid-cols-2" : "md:grid-cols-3"
          }`}
        >
          {cards.map((card, i) => (
            <li key={card.href} className="flex min-w-0">
              <Reveal delay={Math.min(i * 0.08, 0.24)} className="flex w-full">
                <a
                  href={card.href}
                  className="group flex w-full flex-col rounded-xl border-2 border-fwd-ink bg-white p-6 md:p-7 shadow-[4px_4px_0_#111118] motion-safe:transition-transform motion-safe:hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-fwd-red"
                >
                  <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase mb-3">
                    {card.label}
                  </span>
                  <span className="font-display text-xl md:text-[1.35rem] leading-snug text-fwd-ink mb-6">
                    {card.teaser}
                  </span>
                  <span className="mt-auto text-sm font-semibold text-fwd-ink underline decoration-fwd-red decoration-2 underline-offset-4 group-hover:text-fwd-red transition-colors">
                    {card.cta}&nbsp;<span aria-hidden="true">&rarr;</span>
                  </span>
                </a>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
